import { LegalEntityAmlFormModel } from '../../../../contact/components/legal-entity-aml-form/LegalEntityAmlForm';
import {
  AssignmentWizardData, GoodsSource, WizardContact,
} from '../../../../providers/api/dtos';
import { raise } from '../../../../utils';
import { boolToBooleanString, booleanStringToBool } from '../../../../utils/boolean-string/booleanString';
import { NonLegalSellerAmlFormModel } from './non-legal-seller-aml-form/NonLegalSellerAmlForm';

function toGoodsSources(antiMoneyLaunderingFormModel: NonLegalSellerAmlFormModel): GoodsSource[] {
  const goodsSources = [];
  if (antiMoneyLaunderingFormModel.sellingPersonalPropertiesGoodsSource) {
    goodsSources.push(GoodsSource.SELLING_PERSONAL_PROPERTIES);
  }
  if (antiMoneyLaunderingFormModel.thirdPartyIncomeGoodsSource) {
    goodsSources.push(GoodsSource.THIRD_PARTY_INCOME);
  }
  if (antiMoneyLaunderingFormModel.successionOrDonationGoodsSource) {
    goodsSources.push(GoodsSource.SUCCESSION_OR_DONATION);
  }
  if (antiMoneyLaunderingFormModel.otherGoodsSource) {
    goodsSources.push(GoodsSource.OTHER);
  }
  return goodsSources;
}

export const wizardContactToNonLegalSellerAmlFormModel = (seller: WizardContact): Partial<NonLegalSellerAmlFormModel> => {
  const { antiMoneyLaundering: aml } = seller;

  return {
    politicallyExposed: boolToBooleanString(aml?.politicallyExposed),
    relatedToAPoliticallyExposedFigure: boolToBooleanString(aml?.relatedToAPoliticallyExposedFigure),
    sellingPersonalPropertiesGoodsSource: aml?.goodsSources?.some((goodsSource: GoodsSource) => goodsSource === GoodsSource.SELLING_PERSONAL_PROPERTIES),
    thirdPartyIncomeGoodsSource: aml?.goodsSources?.some((goodsSource: GoodsSource) => goodsSource === GoodsSource.THIRD_PARTY_INCOME),
    successionOrDonationGoodsSource: aml?.goodsSources?.some((goodsSource: GoodsSource) => goodsSource === GoodsSource.SUCCESSION_OR_DONATION),
    otherGoodsSource: aml?.goodsSources?.some((goodsSource: GoodsSource) => goodsSource === GoodsSource.OTHER) || false,
    otherGoodsSourceValue: aml?.otherGoodsSourceValue,
    notes: aml?.notes,
  };
};

export const nonLegalSellerAmlFormModelToWizardContact = (seller: WizardContact, formModel: NonLegalSellerAmlFormModel): WizardContact => ({
  ...seller,
  antiMoneyLaundering: {
    politicallyExposed: booleanStringToBool(formModel.politicallyExposed),
    relatedToAPoliticallyExposedFigure: booleanStringToBool(formModel.relatedToAPoliticallyExposedFigure),
    goodsSources: toGoodsSources(formModel),
    otherGoodsSourceValue: formModel.otherGoodsSourceValue?.trim() || undefined,
    notes: formModel.notes?.trim() || undefined,
  },
});

const requireOnlyOneLegalSeller = (sellers: WizardContact[] = []) => {
  if (sellers.length !== 1 || !sellers[0].legal) {
    raise('there must be only one seller and it must be legal');
  }
};

export const assignmentWizardDataToLegalEntityAmlFormModel = (assignmentWizard: AssignmentWizardData): Partial<LegalEntityAmlFormModel> => {
  requireOnlyOneLegalSeller(assignmentWizard.contacts);

  const isCompanyHolder = typeof assignmentWizard.excludeAntiMoneyLaundering === 'undefined'
    ? ''
    : boolToBooleanString(!assignmentWizard.excludeAntiMoneyLaundering);

  return {
    isCompanyHolder,
    notes: assignmentWizard.contacts?.[0].antiMoneyLaundering?.notes || '',
  };
};

export const legalEntityAmlFormModelToAssignmentWizardData = (assignmentWizard: AssignmentWizardData, formValues: LegalEntityAmlFormModel): AssignmentWizardData => {
  requireOnlyOneLegalSeller(assignmentWizard.contacts);

  const excludeAntiMoneyLaundering = formValues.isCompanyHolder === ''
    ? booleanStringToBool(formValues.isCompanyHolder)
    : !booleanStringToBool(formValues.isCompanyHolder);

  return {
    ...assignmentWizard,
    contacts: [{
      ...assignmentWizard.contacts?.[0],
      antiMoneyLaundering: {
        ...assignmentWizard.contacts?.[0].antiMoneyLaundering,
        notes: formValues.notes.trim() || undefined,
      },
    }],
    excludeAntiMoneyLaundering,
  };
};
